import {
  Alert,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  IntegrationAccountDto,
  IntegrationType,
  CreateIntegrationAccountDto,
  UpdateIntegrationAccountDto,
  useCreateIntegrationAccountMutation,
  useUpdateIntegrationAccountMutation,
} from "../../../api/integrationAccountsApi";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";

interface IntegrationAccountModalProps {
  open: boolean;
  account: IntegrationAccountDto | null;
  mode: "add" | "edit";
  onClose: () => void;
}

interface FormValues {
  integrationType: IntegrationType;
  accountName: string;
  apiKey: string;
  additionalConfig: string;
  isActive: boolean;
}

const IntegrationAccountModal: React.FC<IntegrationAccountModalProps> = ({
  open,
  account,
  mode,
  onClose,
}) => {
  const isEditing = mode === "edit";

  const [submitError, setSubmitError] = useState<string | null>(null);

  const [createAccount, { isLoading: isCreating }] =
    useCreateIntegrationAccountMutation();
  const [updateAccount, { isLoading: isUpdating }] =
    useUpdateIntegrationAccountMutation();

  const validationSchema = Yup.object({
    integrationType: Yup.number()
      .required("Integration type is required")
      .oneOf(
        [
          IntegrationType.Samsara,
          IntegrationType.EvaEld,
          IntegrationType.GPSTracking,
          IntegrationType.AnyTrack,
          IntegrationType.Motive,
          IntegrationType.Other,
        ],
        "Invalid integration type"
      ),
    accountName: Yup.string()
      .required("Account name is required")
      .min(3, "Account name must be at least 3 characters")
      .max(100, "Account name cannot exceed 100 characters"),
    apiKey: Yup.string()
      .required("API key is required")
      .max(500, "API key cannot exceed 500 characters"),
    additionalConfig: Yup.string().max(
      2000,
      "Additional config cannot exceed 2000 characters"
    ),
    isActive: Yup.boolean(),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      integrationType: IntegrationType.Samsara,
      accountName: "",
      apiKey: "",
      additionalConfig: "",
      isActive: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEditing && account) {
          const updateData: UpdateIntegrationAccountDto = {
            integrationType: values.integrationType,
            accountName: values.accountName,
            apiKey: values.apiKey,
            additionalConfig: values.additionalConfig || undefined,
            isActive: values.isActive,
          };
          await updateAccount({ id: account.id, data: updateData }).unwrap();
        } else {
          const createData: CreateIntegrationAccountDto = {
            integrationType: values.integrationType,
            accountName: values.accountName,
            apiKey: values.apiKey,
            additionalConfig: values.additionalConfig || undefined,
            isActive: values.isActive,
          };
          await createAccount(createData).unwrap();
        }
        setSubmitError(null);
        onClose();
      } catch (error: any) {
        console.error("Failed to save integration account:", error);
        const message =
          error?.data?.message ||
          error?.data?.title ||
          error?.error ||
          "An unexpected error occurred. Please try again.";
        setSubmitError(message);
      }
    },
  });

  // Reset form when modal opens/closes or account changes
  useEffect(() => {
    if (open) {
      if (isEditing && account) {
        formik.resetForm({
          values: {
            integrationType: account.integrationType,
            accountName: account.accountName,
            apiKey: account.apiKey,
            additionalConfig: account.additionalConfig || "",
            isActive: account.isActive,
          },
        });
      } else {
        formik.resetForm({
          values: {
            integrationType: IntegrationType.Samsara,
            accountName: "",
            apiKey: "",
            additionalConfig: "",
            isActive: true,
          },
        });
      }
    }
    setSubmitError(null);
  }, [open, account, isEditing]);

  const integrationTypeOptions = [
    { value: IntegrationType.Samsara, label: "Samsara" },
    { value: IntegrationType.Motive, label: "Motive (KeepTruckin)" },
    { value: IntegrationType.EvaEld, label: "EvaEld" },
    { value: IntegrationType.GPSTracking, label: "GPS Tracking" },
    { value: IntegrationType.AnyTrack, label: "AnyFleet Pro (AnyTrack)" },
    { value: IntegrationType.Other, label: "Other" },
  ];

  return (
    <FormDialog
      open={open}
      title={
        isEditing
          ? `Edit Integration Account: ${account?.accountName}`
          : "Add Integration Account"
      }
      onClose={onClose}
      onSubmit={formik.handleSubmit}
      isLoading={isCreating || isUpdating}
      submitLabel={
        isCreating || isUpdating
          ? "Syncing..."
          : isEditing
            ? "Save Changes"
            : "Add Account"
      }
      maxWidth="sm"
    >
      {/* Submit Error */}
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {/* Account Info Display (Edit Mode) */}
      {isEditing && account && (
        <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Account Information
          </Typography>
          <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={account.integrationTypeName}
              color="primary"
              size="small"
              variant="outlined"
            />
            <Chip
              label={account.isActive ? "Active" : "Inactive"}
              color={account.isActive ? "success" : "default"}
              size="small"
            />
          </Box>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="integrationType-label">
              Integration Type
            </InputLabel>
            <Select
              labelId="integrationType-label"
              id="integrationType"
              name="integrationType"
              value={formik.values.integrationType}
              label="Integration Type"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.integrationType &&
                Boolean(formik.errors.integrationType)
              }
            >
              {integrationTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.integrationType &&
              formik.errors.integrationType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formik.errors.integrationType}
                </Typography>
              )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            margin="normal"
            id="accountName"
            name="accountName"
            label="Account Name"
            placeholder="e.g., Samsara - Fleet A"
            value={formik.values.accountName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.accountName && Boolean(formik.errors.accountName)
            }
            helperText={formik.touched.accountName && formik.errors.accountName}
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            margin="normal"
            id="apiKey"
            name="apiKey"
            label="API Key"
            placeholder="Enter your API key or access token"
            value={formik.values.apiKey}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.apiKey && Boolean(formik.errors.apiKey)}
            helperText={formik.touched.apiKey && formik.errors.apiKey}
            size="small"
            type="password"
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            margin="normal"
            id="additionalConfig"
            name="additionalConfig"
            label="Additional Configuration (JSON)"
            placeholder='{"baseUrl": "https://api.example.com", "orgId": "123"}'
            value={formik.values.additionalConfig}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.additionalConfig &&
              Boolean(formik.errors.additionalConfig)
            }
            helperText={
              (formik.touched.additionalConfig &&
                formik.errors.additionalConfig) ||
              "Optional: JSON configuration for this integration"
            }
            size="small"
            multiline
            rows={3}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                id="isActive"
                name="isActive"
                checked={formik.values.isActive}
                onChange={formik.handleChange}
                color="primary"
              />
            }
            label="Active"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Only active accounts will be used for data synchronization
          </Typography>
        </Grid>
      </Grid>
    </FormDialog>
  );
};

export default IntegrationAccountModal;

